import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  styled,
  IconButton,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ChevronLeft } from '@mui/icons-material';

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  position: 'sticky',
  top: 0,
  backgroundColor: theme.palette.background.paper,
  zIndex: 1,
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  overflow: 'auto',
  maxHeight: '80vh',
  '&::-webkit-scrollbar': {
    width: '0px',
    background: 'transparent', // Optional: just make scrollbar invisible
  },
  scrollbarWidth: 'none' /* Firefox */,
  '-ms-overflow-style': 'none' /* Internet Explorer and Edge */,
  '&::-webkit-scrollbar-thumb': {
    background: 'transparent', // Optional: hide scrollbar thumb
  },
  padding: '0px 15px',
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  position: 'sticky',
  bottom: 0,
  backgroundColor: theme.palette.background.paper,
  zIndex: 1,
  padding: '10px',
  display: 'flex',
  justifyContent: 'flex-end', // Align buttons to the right
  gap: '10px',
}));

const GenericModal = ({
  open,
  onClose,
  title,
  children,
  primaryButtonText = 'Save',
  secondaryButtonText = 'Cancel',
  onPrimaryAction,
  onSecondaryAction,
  primaryButtonProps,
  secondaryButtonProps,
  maxWidth = 'sm',
  fullWidth = true,
  showCloseIcon = false,
  primaryButtonColor = 'primaryBtn',
  secondaryButtonColor = 'cancel',
  primaryButtonVariant = 'contained',
  secondaryButtonVariant = 'outlined',
  isLoading = false,
  isDanger = false,
  isButtonLeft = false,
  onTitleButtonClick,
  desktopWidth = '447px',
  removePrimaryButton = false,
  disabled = false,
  propZindex,
  buttonsInHeader = false,
  hideFooter = false,
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const handlePrimaryAction = () => {
    if (onPrimaryAction) {
      onPrimaryAction();
    }
  };

  const handleSecondaryAction = () => {
    if (onSecondaryAction) {
      onSecondaryAction();
    }
    onClose();
  };

  const dialogStyle = {
    ...(isDesktop && desktopWidth
      ? {
          '& .MuiDialog-paper': {
            width: desktopWidth,
            maxWidth: desktopWidth,
            borderRadius: '1rem',
          },
        }
      : {}),
    zIndex: propZindex,
  };

  // Action buttons component to be reused in header or footer
  const ActionButtons = () => (
    <>
      <Button
        onClick={handleSecondaryAction}
        color={secondaryButtonColor}
        disabled={disabled || secondaryButtonProps?.disabled}
        variant={secondaryButtonVariant}
        {...secondaryButtonProps}
        sx={{
          minWidth: '120px', // Set a minimum width
          height: '36px', // Set a fixed height
        }}
      >
        {secondaryButtonText}
      </Button>
      {!removePrimaryButton && (
        <Button
          onClick={handlePrimaryAction}
          color={primaryButtonColor}
          disabled={disabled || isLoading || primaryButtonProps?.disabled}
          variant={primaryButtonVariant}
          {...primaryButtonProps}
          sx={{
            color: '#fff',
            minWidth: '120px', // Set a minimum width
            height: '36px', // Set a fixed height
            '&:hover': {
              backgroundColor: theme.palette.primaryBtn.main, // Use the hover color we defined earlier
              opacity: 0.9, // Optional: reduce opacity on hover for a subtle effect
            },
            '&.Mui-disabled': {
              backgroundColor: '#25865e',
              color: 'white',
            },
          }}
        >
          {isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            primaryButtonText
          )}
        </Button>
      )}
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      aria-labelledby="modal-title"
      sx={dialogStyle}
      className="bg-white/40 backdrop-blur-sm transition-all duration-200 ease-in-out rounded-xl"
    >
      <StyledDialogTitle
        id="modal-title"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div className="flex items-center">
          {isButtonLeft ? (
            <button
              onClick={onTitleButtonClick}
              style={{
                borderRadius: '100%',
                width: '2.25rem !important',
                height: '2.25rem !important',
                backgroundColor: '#EBEFF6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '2px !important',
                margin: '2px !important',
              }}
            >
              <ChevronLeft className="w-[1.5rem] h-[1.5rem] text-mediumBlue" />
            </button>
          ) : (
            <h2
              className="font-bold text-base m-0"
              style={{ margin: '0 !important' }}
            >
              {title}
            </h2>
          )}
        </div>

        <div className="flex items-center gap-2">
          {buttonsInHeader && <ActionButtons />}

          {showCloseIcon && (
            <IconButton aria-label="close" onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          )}
        </div>
      </StyledDialogTitle>

      <StyledDialogContent dividers sx={{ padding: '15px' }}>
        {children}
      </StyledDialogContent>

      {!buttonsInHeader && !hideFooter && (
        <StyledDialogActions>
          <ActionButtons />
        </StyledDialogActions>
      )}
    </Dialog>
  );
};

export default GenericModal;
